import { Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@compass/core";
import {
  TextField,
  Chip,
  InputAdornment,
  IconButton,
  Box,
} from "@mui/material";
import { StudentCustomIdType } from "generated/openApi";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import CloseIcon from "@mui/icons-material/Close";

export const StudentCustomIdsPaster = (props: {
  typeId?: number;
  value?: string[];
  onChange?: (customIdTypeId: number | undefined, customIds: string[]) => void;
  onAdd?: (customIdTypeId: number | undefined, customIds: string[]) => void;
}) => {
  const splitRegex = /[ ,\r\n\t]+/;
  const [inputValue, setInputValue] = useState("");
  const [internalIds, setInternalIds] = useState<string[]>([]);
  const [selectedCustomIdTypeId, setSelectedCustomIdTypeId] = useState<number>(1);
  const [customIdTypes, setCustomIdTypes] = useState<StudentCustomIdType[]>();

  const selectedCustomIdType = customIdTypes?.find((x) => x.id === selectedCustomIdTypeId);

  let label: string | undefined = selectedCustomIdType
    ? `${selectedCustomIdType.name}s`
    : "Student IDs";
  if (props.onAdd) label = `Add by ${label}`;

  useEffect(() => {
    ApiService.students.getCustomIdTypes().then((response) => {
      setCustomIdTypes(response);
    });
  }, []);

  useEffect(() => {
    if (props.typeId) setSelectedCustomIdTypeId(props.typeId);
  }, [props.typeId]);

  const ids = props.value ?? internalIds;

  const updateIds = (updated: string[]) => {
    if (props.value === undefined) {
      setInternalIds(updated);
    }

    props.onChange?.(selectedCustomIdTypeId, updated);
  };

  const addIds = (raw: string) => {
    const newIds = raw.split(splitRegex).filter((x) => x);
    if (newIds.length > 0) {
      const updated = [...ids, ...newIds];
      updateIds(updated);
    }
    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === " " || e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addIds(inputValue);
    } else if (e.key === "Backspace" && inputValue === "" && ids.length > 0) {
      const updated = ids.slice(0, -1);
      updateIds(updated);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    addIds(pastedText);
  };

  const handleClear = () => {
    setInputValue("");
    updateIds([]);
  };

  const count = ids.length;

  return (
    <Stack spacing="sm">
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.5,
          border: "1px solid rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
          padding: "6px 8px",
          "&:focus-within": {
            borderColor: (theme) => theme.palette.primary.main,
            boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}33`,
          },
        }}
      >
        {ids.map((id, index) => (
          <Chip key={index} label={id} size="small" color="primary" />
        ))}

        <Box sx={{ flexGrow: 1, minWidth: "120px" }}>
          <TextField
            label={ids.length === 0 ? label : undefined}
            variant="standard"
            placeholder={ids.length === 0 ? `Type or paste ${selectedCustomIdType?.name}s...` : ""}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            InputProps={{
              disableUnderline: true,
              endAdornment:
                ids.length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClear}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
            fullWidth
          />
        </Box>
      </Box>

      <ToggleButtonGroup value={selectedCustomIdTypeId} exclusive size="small">
        {customIdTypes?.map((option) => (
          <ToggleButton
            key={option.id}
            value={option.id}
            onMouseDown={() => setSelectedCustomIdTypeId(option.id)}
          >
            {option.name}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {(ids.length > 0 || props.onAdd) && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing="sm" alignItems="center">
            {count > 0 && (
              <Typography variant="caption">
                <b>{count}</b> {selectedCustomIdType?.name}s
              </Typography>
            )}

            {props.onAdd && (
              <Button
                onClick={() => {
                  props.onAdd?.(selectedCustomIdTypeId, ids);
                  if (props.value === undefined) {
                    setInternalIds([]);
                  }
                  setInputValue("");
                }}
                variant="outlined"
                size="small"
              >
                + Add
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
